import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, Checkbox, FormControlLabel,
  Accordion, AccordionSummary, AccordionDetails, Chip, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Alert, CircularProgress, Divider, Tooltip, TablePagination
} from '@mui/material';
import {
  ExpandMore, Edit, PersonAdd, Add, Refresh, Shield,
  CheckBox, CheckBoxOutlineBlank
} from '@mui/icons-material';
import api, { roleService, userService } from '../services/api';
import RoleAssignments from './RoleAssignments';
import { usePagination } from '../hooks/usePagination';

interface Role {
  id: number;
  name: string;
  description: string;
  parentRoleId?: number;
  parentRoleName?: string;
  subRoles: Role[];
  permissions: string[];
  isActive: boolean;
  createdAt: string;
}

interface Permission {
  id: number;
  name: string;
  description: string;
  module: string;
}

interface User {
  id: number;
  fullName: string;
  email: string;
  role: string;
}

// Icon colour per module
const MODULE_COLORS: Record<string, string> = {
  Timesheet:    '#1976d2',
  Leave:        '#388e3c',
  Attendance:   '#f57c00',
  Payroll:      '#7b1fa2',
  HR:           '#c62828',
  Goals:        '#0288d1',
  Projects:     '#00796b',
  Users:        '#5d4037',
  Reports:      '#455a64',
  Clients:      '#1565c0',
  Roles:        '#6a1b9a',
  Holidays:     '#2e7d32',
  Organization: '#37474f',
};

const emptyForm = { name: '', description: '', parentRoleId: '', permissionIds: [] as number[] };

const RoleManagement: React.FC = () => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState('');

  const [showCreateRole, setShowCreateRole] = useState(false);
  const [showAssignRole, setShowAssignRole] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);

  const [form, setForm] = useState({ ...emptyForm });
  const [roleAssignment, setRoleAssignment] = useState({ userId: '', roleId: '', managerId: '' });

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([fetchRoles(), fetchPermissions(), fetchUsers()]);
    } catch {
      setError('Failed to load data. Please check if the API is running.');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    const data = await roleService.getRoles();
    setRoles(data || []);
  };

  const fetchPermissions = async () => {
    const data = await roleService.getPermissions();
    setPermissions(data || []);
  };

  const fetchUsers = async () => {
    const data = await userService.getUsers();
    setUsers(data || []);
  };

  const showSuccess = (msg: string) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 4000);
  };

  const handleSeedPermissions = async () => {
    setSeeding(true);
    try {
      const res = await api.post('/roles/seed-permissions');
      await fetchPermissions();
      showSuccess(res.data.message);
    } catch {
      setError('Failed to seed permissions.');
    } finally {
      setSeeding(false);
    }
  };

  // ── Permission helpers ──────────────────────────────────────────────────────

  const groupedPermissions = permissions.reduce<Record<string, Permission[]>>((acc, p) => {
    (acc[p.module] = acc[p.module] || []).push(p);
    return acc;
  }, {});

  const togglePermission = (id: number) => {
    setForm(prev => ({
      ...prev,
      permissionIds: prev.permissionIds.includes(id)
        ? prev.permissionIds.filter(x => x !== id)
        : [...prev.permissionIds, id]
    }));
  };

  const toggleModule = (module: string) => {
    const moduleIds = (groupedPermissions[module] || []).map(p => p.id);
    const allSelected = moduleIds.every(id => form.permissionIds.includes(id));
    setForm(prev => ({
      ...prev,
      permissionIds: allSelected
        ? prev.permissionIds.filter(id => !moduleIds.includes(id))
        : Array.from(new Set([...prev.permissionIds, ...moduleIds]))
    }));
  };

  const moduleChecked = (module: string): boolean => {
    const ids = (groupedPermissions[module] || []).map(p => p.id);
    return ids.length > 0 && ids.every(id => form.permissionIds.includes(id));
  };

  const moduleIndeterminate = (module: string): boolean => {
    const ids = (groupedPermissions[module] || []).map(p => p.id);
    const selected = ids.filter(id => form.permissionIds.includes(id));
    return selected.length > 0 && selected.length < ids.length;
  };

  const selectAll = () => setForm(prev => ({ ...prev, permissionIds: permissions.map(p => p.id) }));
  const clearAll = () => setForm(prev => ({ ...prev, permissionIds: [] }));

  // ── CRUD ────────────────────────────────────────────────────────────────────

  const openCreate = () => {
    setForm({ ...emptyForm });
    setEditingRole(null);
    setShowCreateRole(true);
  };

  const openEdit = (role: Role) => {
    const currentIds = permissions.filter(p => role.permissions.includes(p.name)).map(p => p.id);
    setForm({
      name: role.name,
      description: role.description,
      parentRoleId: role.parentRoleId?.toString() || '',
      permissionIds: currentIds
    });
    setEditingRole(role);
    setShowCreateRole(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingRole) {
        await api.put(`/roles/${editingRole.id}`, {
          name: form.name,
          description: form.description,
          parentRoleId: form.parentRoleId ? parseInt(form.parentRoleId) : null,
          permissionIds: form.permissionIds
        });
        await api.put(`/roles/${editingRole.id}/permissions`, form.permissionIds);
        showSuccess('Role updated successfully.');
      } else {
        await roleService.createRole({
          ...form,
          parentRoleId: form.parentRoleId ? parseInt(form.parentRoleId) : null
        });
        showSuccess('Role created successfully.');
      }
      setShowCreateRole(false);
      setEditingRole(null);
      setForm({ ...emptyForm });
      fetchRoles();
    } catch {
      setError('Failed to save role.');
    }
  };

  const handleAssignRole = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await roleService.assignRole(
        parseInt(roleAssignment.userId),
        parseInt(roleAssignment.roleId),
        roleAssignment.managerId ? parseInt(roleAssignment.managerId) : undefined
      );
      setShowAssignRole(false);
      setRoleAssignment({ userId: '', roleId: '', managerId: '' });
      showSuccess('Role assigned successfully.');
    } catch {
      setError('Error assigning role. The user may already have this role.');
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  const renderPermissionsPanel = () => (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle2" color="text.secondary">
          {form.permissionIds.length} / {permissions.length} permissions selected
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" onClick={selectAll} startIcon={<CheckBox fontSize="small" />}>All</Button>
          <Button size="small" onClick={clearAll} startIcon={<CheckBoxOutlineBlank fontSize="small" />}>None</Button>
        </Box>
      </Box>

      {permissions.length === 0 && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          No permissions found. Click "Seed Permissions" above to load all available permissions.
        </Alert>
      )}

      <Box sx={{ maxHeight: 420, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        {Object.entries(groupedPermissions).map(([module, perms]) => {
          const color = MODULE_COLORS[module] || '#607d8b';
          const selectedCount = perms.filter(p => form.permissionIds.includes(p.id)).length;
          return (
            <Accordion key={module} disableGutters elevation={0}
              sx={{ '&:before': { display: 'none' }, borderBottom: '1px solid', borderColor: 'divider' }}>
              <AccordionSummary expandIcon={<ExpandMore />} sx={{ bgcolor: 'grey.50', minHeight: 44, '& .MuiAccordionSummary-content': { my: 0.5, alignItems: 'center', gap: 1 } }}>
                <Checkbox
                  checked={moduleChecked(module)}
                  indeterminate={moduleIndeterminate(module)}
                  onChange={() => toggleModule(module)}
                  onClick={e => e.stopPropagation()}
                  size="small"
                  sx={{ p: 0.5, color }}
                />
                <Chip label={module} size="small" sx={{ bgcolor: color, color: 'white', fontWeight: 'bold', fontSize: '0.7rem' }} />
                <Typography variant="body2" sx={{ ml: 0.5, color: 'text.secondary', fontSize: '0.75rem' }}>
                  {selectedCount}/{perms.length}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 2, py: 1 }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 0.5 }}>
                  {perms.map(perm => (
                    <Tooltip key={perm.id} title={perm.description} placement="top" arrow>
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={form.permissionIds.includes(perm.id)}
                            onChange={() => togglePermission(perm.id)}
                            sx={{ py: 0.5, color }}
                          />
                        }
                        label={<Typography variant="body2" sx={{ fontSize: '0.82rem' }}>{perm.name}</Typography>}
                        sx={{ mx: 0 }}
                      />
                    </Tooltip>
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
    </Box>
  );

  // Called unconditionally (rules of hooks) even before the `loading` early return.
  const { pagedRows: pagedRoles, paginationProps: rolesPaginationProps } = usePagination(roles);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5" fontWeight="bold">Role Management</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Load all app permissions into the database (run once)">
            <Button
              variant="outlined"
              startIcon={seeding ? <CircularProgress size={16} /> : <Refresh />}
              onClick={handleSeedPermissions}
              disabled={seeding}
              size="small"
            >
              Seed Permissions
            </Button>
          </Tooltip>
          <Button variant="outlined" startIcon={<PersonAdd />} onClick={() => setShowAssignRole(true)} size="small">
            Assign Role
          </Button>
          <Button variant="contained" startIcon={<Add />} onClick={openCreate} size="small">
            Create Role
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {successMsg && <Alert severity="success" sx={{ mb: 2 }}>{successMsg}</Alert>}

      {/* Role list */}
      <TableContainer component={Paper} elevation={2}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Role</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Parent Role</TableCell>
              <TableCell>Permissions</TableCell>
              <TableCell sx={{ width: 80 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {roles.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No roles found. Create one to get started.
                </TableCell>
              </TableRow>
            ) : pagedRoles.map(role => (
              <TableRow key={role.id} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Shield fontSize="small" sx={{ color: 'primary.main' }} />
                    <Typography variant="body2" fontWeight="bold">{role.name}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">{role.description}</Typography>
                </TableCell>
                <TableCell>
                  {role.parentRoleName
                    ? <Chip label={role.parentRoleName} size="small" variant="outlined" />
                    : <Typography variant="body2" color="text.disabled">—</Typography>}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 400 }}>
                    {role.permissions.length === 0
                      ? <Typography variant="caption" color="text.disabled">No permissions</Typography>
                      : role.permissions.slice(0, 5).map(p => (
                          <Chip key={p} label={p} size="small" sx={{ fontSize: '0.7rem', height: 20 }} />
                        ))
                    }
                    {role.permissions.length > 5 && (
                      <Chip
                        label={`+${role.permissions.length - 5} more`}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.7rem', height: 20 }}
                      />
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Tooltip title="Edit role & permissions">
                    <IconButton size="small" onClick={() => openEdit(role)}>
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {roles.length > 0 && <TablePagination {...rolesPaginationProps} />}
      </TableContainer>

      <Divider sx={{ my: 3 }} />
      <RoleAssignments />

      {/* Create / Edit Role Dialog */}
      <Dialog open={showCreateRole} onClose={() => setShowCreateRole(false)} maxWidth="md" fullWidth
        slotProps={{ paper: { sx: { maxHeight: '92vh' } } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Shield color="primary" />
            {editingRole ? `Edit Role — ${editingRole.name}` : 'Create New Role'}
          </Box>
        </DialogTitle>
        <Divider />
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ pt: 2 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
              <TextField
                label="Role Name"
                value={form.name}
                onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                required
                size="small"
                fullWidth
              />
              <TextField
                select
                label="Parent Role"
                value={form.parentRoleId}
                onChange={e => setForm(prev => ({ ...prev, parentRoleId: e.target.value }))}
                size="small"
                fullWidth
              >
                <MenuItem value="">No Parent</MenuItem>
                {roles.filter(r => r.id !== editingRole?.id).map(role => (
                  <MenuItem key={role.id} value={role.id}>{role.name}</MenuItem>
                ))}
              </TextField>
            </Box>

            <TextField
              label="Description"
              value={form.description}
              onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
              required
              size="small"
              fullWidth
              multiline
              rows={2}
              sx={{ mb: 2 }}
            />

            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              Permissions
            </Typography>
            {renderPermissionsPanel()}
          </DialogContent>
          <Divider />
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={() => { setShowCreateRole(false); setEditingRole(null); }}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editingRole ? 'Update Role' : 'Create Role'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Assign Role Dialog */}
      <Dialog open={showAssignRole} onClose={() => setShowAssignRole(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Role to Employee</DialogTitle>
        <Divider />
        <form onSubmit={handleAssignRole}>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              select
              label="Employee"
              value={roleAssignment.userId}
              onChange={e => setRoleAssignment(prev => ({ ...prev, userId: e.target.value }))}
              required
              size="small"
              fullWidth
            >
              <MenuItem value="">Select Employee</MenuItem>
              {users.map(user => (
                <MenuItem key={user.id} value={user.id}>
                  {user.fullName} — {user.email}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Role"
              value={roleAssignment.roleId}
              onChange={e => setRoleAssignment(prev => ({ ...prev, roleId: e.target.value }))}
              required
              size="small"
              fullWidth
            >
              <MenuItem value="">Select Role</MenuItem>
              {roles.map(role => (
                <MenuItem key={role.id} value={role.id}>{role.name}</MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Reports To (Manager)"
              value={roleAssignment.managerId}
              onChange={e => setRoleAssignment(prev => ({ ...prev, managerId: e.target.value }))}
              size="small"
              fullWidth
              helperText="Optional — select who this person reports to"
            >
              <MenuItem value="">No Manager</MenuItem>
              {users.map(user => (
                <MenuItem key={user.id} value={user.id}>{user.fullName}</MenuItem>
              ))}
            </TextField>
          </DialogContent>
          <Divider />
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={() => setShowAssignRole(false)}>Cancel</Button>
            <Button type="submit" variant="contained">Assign Role</Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default RoleManagement;
